// Dream and entry analysis — the sparkle, the lens it opens, and the sheet the reading streams into.
//
// Ported from iOS's EntryReflectionSheet. The two follow-ups are the ones the phone offers and they
// are both deliberate: a reading is worth little if it dead-ends. "Talk this through" carries it into
// a real conversation, and "Add to your portrait" folds it into the entry — which is how it reaches
// the nightly Archetype portrait, since the portrait is built from entries and there is no endpoint
// for injecting memory directly.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  analysisErrorText,
  analysisSymbols,
  analyzeEntry,
  detailsSummary,
  EMPTY_ANALYSIS_TEXT,
  isDreamKind,
  kindsFor,
  lens,
  type AnalysisKind,
} from '../api/analysis';
import type { DreamDetails } from '../api/dream-details';
import { IconClose, IconSpark } from '../components/Icons';
import { PrimaryButton } from '../components/ui';

/**
 * The sparkle in the composer toolbar and the lens list it opens.
 *
 * Which lenses are offered follows the entry's mode, not a preference — reading a journal entry
 * "through Carl Jung" would be offering something the prompt behind it does not do.
 */
export function AnalysisMenuButton({
  mode,
  disabled,
  onPick,
}: {
  mode: 'waking' | 'dream';
  /** Nothing written yet. The button stays visible and says why, rather than disappearing. */
  disabled: boolean;
  onPick(kind: AnalysisKind): void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      // Stopped here, not just handled. The composer closes itself on Escape from a listener on
      // `window`, and a document listener runs first — without this, dismissing the menu would also
      // walk out of the entry.
      event.stopPropagation();
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="oanalysis-menu" ref={wrap}>
      {open ? (
        <div className="oanalysis-pop" role="menu">
          {kindsFor(mode).map((kind) => {
            const { title, blurb } = lens(kind);
            return (
              <button
                key={kind}
                role="menuitem"
                disabled={disabled}
                onClick={() => {
                  setOpen(false);
                  onPick(kind);
                }}
              >
                <span className="oanalysis-title">{title}</span>
                <span className="oanalysis-blurb">{blurb}</span>
              </button>
            );
          })}
          {disabled ? (
            <p className="oweave-hint">Write something first and Oreia will read it back to you.</p>
          ) : null}
        </div>
      ) : null}

      <button
        className="oact"
        aria-label={mode === 'dream' ? 'Analyse this dream' : 'Reflect on this entry'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <IconSpark /><span>{mode === 'dream' ? 'Analyse dream' : 'Reflect'}</span>
      </button>
    </div>
  );
}

export interface AnalysisSheetProps {
  /** The lens being read through, or null when the sheet is closed. */
  kind: AnalysisKind | null;
  body: string;
  /** The dream's structured pages. Read alongside the prose so the whole record is analysed. */
  details: DreamDetails;
  onClose(): void;
  /** Carry the reading into a conversation. */
  onDiscuss(prompt: string): void;
  /** Dreams only: fold the reading into the entry so the portrait picks it up. */
  onFold?(heading: string, text: string): void;
  /** Feed chips the dreamer typed, offered as readings alongside the structured pages. */
  extraSymbols?: string[];
}

export function EntryAnalysisSheet({
  kind,
  body,
  details,
  onClose,
  onDiscuss,
  onFold,
  extraSymbols = [],
}: AnalysisSheetProps) {
  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [folded, setFolded] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  /** null is the whole dream; a string is one image from it. */
  const [focus, setFocus] = useState<string | null>(null);

  const source = useRef({ body, details });
  source.current = { body, details };

  const wholeText = useRef('');
  const wholeSucceeded = useRef(false);
  const symbolCache = useRef(new Map<string, string>());

  useEffect(() => {
    if (!kind) return;

    const controller = new AbortController();
    const { signal } = controller;
    const { body: story, details: pages } = source.current;

    wholeText.current = '';
    wholeSucceeded.current = false;
    symbolCache.current = new Map();
    setFocus(null);
    setText('');
    setFolded(false);
    setSucceeded(false);
    setStreaming(true);

    let accumulated = '';
    analyzeEntry({
      kind,
      body: story,
      details: isDreamKind(kind) ? detailsSummary(pages) : '',
      onToken: (token) => {
        if (signal.aborted) return;
        accumulated += token;
        setText(accumulated);
      },
      signal,
    })
      .then(() => {
        if (signal.aborted) return;
        wholeSucceeded.current = Boolean(accumulated.trim());
        setSucceeded(wholeSucceeded.current);
        if (!accumulated.trim()) accumulated = EMPTY_ANALYSIS_TEXT;
        wholeText.current = accumulated;
        setText(accumulated);
      })
      .catch((err) => {
        if (signal.aborted) return;
        if (!accumulated.trim()) accumulated = analysisErrorText(err);
        wholeText.current = accumulated;
        setText(accumulated);
      })
      .finally(() => {
        if (!signal.aborted) setStreaming(false);
      });

    return () => controller.abort();
  }, [kind]);

  useEffect(() => {
    if (!kind || !focus || !wholeText.current) return;

    const cached = symbolCache.current.get(focus);
    if (cached) {
      setText(cached);
      setFolded(false);
      setSucceeded(cached !== EMPTY_ANALYSIS_TEXT);
      setStreaming(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const { body: story, details: pages } = source.current;

    setText('');
    setFolded(false);
    setSucceeded(false);
    setStreaming(true);

    let accumulated = '';
    analyzeEntry({
      kind,
      body: story,
      details: isDreamKind(kind) ? detailsSummary(pages) : '',
      symbol: focus,
      onToken: (token) => {
        if (signal.aborted) return;
        accumulated += token;
        setText(accumulated);
      },
      signal,
    })
      .then(() => {
        if (signal.aborted) return;
        setSucceeded(Boolean(accumulated.trim()));
        if (!accumulated.trim()) accumulated = EMPTY_ANALYSIS_TEXT;
        symbolCache.current.set(focus, accumulated);
        setText(accumulated);
      })
      .catch((err) => {
        if (signal.aborted) return;
        if (!accumulated.trim()) setText(analysisErrorText(err));
      })
      .finally(() => {
        if (!signal.aborted) setStreaming(false);
      });

    return () => controller.abort();
  }, [kind, focus]);

  // Escape closes the reading rather than the entry behind it. Same reason as the menu's: the
  // composer listens on `window`, so this has to stop before it gets there.
  useEffect(() => {
    if (!kind) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [kind, onClose]);

  useEffect(() => {
    if (!kind) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = dialog.current;
    node?.focus();
    function trap(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !node) return;
      const controls = [...node.querySelectorAll<HTMLElement>('button, summary, [href], [tabindex="0"]')].filter((el) => !(el instanceof HTMLButtonElement && el.disabled) && (el.tagName === 'SUMMARY' || !el.closest('details:not([open])')));
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) { event.preventDefault(); node.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === node)) {
        event.preventDefault(); first.focus();
      }
    }
    function keepFocus(event: FocusEvent) {
      if (node && !node.contains(event.target as Node)) node.focus();
    }
    document.addEventListener('keydown', trap);
    document.addEventListener('focusin', keepFocus);
    return () => {
      document.removeEventListener('keydown', trap);
      document.removeEventListener('focusin', keepFocus);
      const target = previous?.isConnected && previous !== document.body
        ? previous : document.querySelector<HTMLElement>('.oanalysis-menu > button');
      target?.focus();
    };
  }, [kind]);

  if (!kind) return null;

  const { heading, blurb } = lens(kind);
  const ready = succeeded && !streaming && text.trim().length > 0;
  const symbols = isDreamKind(kind) ? analysisSymbols(details, extraSymbols) : [];
  const lookingAt = focus ?? 'the whole dream';

  const showWhole = () => {
    setFocus(null);
    setFolded(false);
    setSucceeded(wholeSucceeded.current);
    if (wholeText.current) setText(wholeText.current);
    setStreaming(false);
  };

  return createPortal(
    <>
      <div className="orset-scrim oanalysis-scrim" onClick={onClose} />
      <div className="orset oanalysis" ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={heading}>
        <div className="orset-head">
          <h2>{heading}</h2>
          <button className="orset-x" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="orset-body">
          <p className="oanalysis-lede">{focus ? `A closer look at ${focus}.` : blurb}</p>

          <div className="oanalysis-text" aria-live="polite" aria-busy={streaming}>
            {text ? (
              text
                .split(/\n{2,}/)
                .filter((p) => p.trim())
                .map((paragraph, i) => <p key={i}>{paragraph.trim()}</p>)
            ) : (
              <p className="oanalysis-waiting">
                {focus ? `Sitting with ${focus}…` : 'Reading what you wrote…'}
              </p>
            )}
            {streaming && text ? <span className="oanalysis-caret" aria-hidden /> : null}
          </div>
          <div className="oanalysis-actions">
            {symbols.length > 0 ? (
              <details className="oanalysis-symbols">
                <summary>Explore an image from this dream</summary>
                <div className="odchips" role="group" aria-label="Images in this dream">
                  <button
                    className={`ochip${focus === null ? ' active' : ''}`}
                    aria-pressed={focus === null}
                    disabled={streaming}
                    onClick={showWhole}
                  >
                    The whole dream
                  </button>
                  {symbols.map((symbol) => (
                    <button
                      key={symbol}
                      className={`ochip${focus === symbol ? ' active' : ''}`}
                      aria-pressed={focus === symbol}
                      disabled={streaming}
                      onClick={() => setFocus(symbol)}
                    >
                      {symbol}
                    </button>
                  ))}
                </div>
              </details>
            ) : null}

            <PrimaryButton
              disabled={!ready}
              onClick={() =>
                onDiscuss(
                  `About my ${isDreamKind(kind) ? 'dream' : 'entry'} — you said, looking at ${lookingAt}:\n\n${text.trim()}\n\n`,
                )
              }
            >
              Talk this through
            </PrimaryButton>

            {onFold ? (
              <button
                className="oanalysis-fold"
                disabled={!ready || folded}
                onClick={() => {
                  onFold(focus ? `${heading} — ${focus}` : heading, text.trim());
                  setFolded(true);
                }}
              >
                {folded ? 'Added to your portrait' : 'Add to your portrait'}
              </button>
            ) : null}

            {onFold ? (
              <p className="oanalysis-note">
                Saves this reading in your entry for your overnight portrait.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
